import { useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, List, ListOrdered, Link as LinkIcon } from "lucide-react";

const TOOLS = [
  { cmd: "bold", icon: Bold, label: "Bold" },
  { cmd: "italic", icon: Italic, label: "Italic" },
  { cmd: "insertUnorderedList", icon: List, label: "Bullet list" },
  { cmd: "insertOrderedList", icon: ListOrdered, label: "Numbered list" },
];

function toHtml(value) {
  if (!value) return "";
  if (/<[a-z][\s\S]*>/i.test(value)) return value;
  return value
    .split(/\n\n+/)
    .map((p) => `<p>${p.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`)
    .join("");
}

export function RichTextEditor({ value, onChange, testid }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = DOMPurify.sanitize(toHtml(value), { ADD_ATTR: ["target", "rel"] });
    // init once per mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => onChange(ref.current?.innerHTML || "");

  const exec = (cmd) => {
    ref.current?.focus();
    document.execCommand(cmd, false, null);
    emit();
  };

  const addLink = () => {
    ref.current?.focus();
    const url = window.prompt("Enter the full URL (including https://)");
    if (url) {
      document.execCommand("createLink", false, url);
      emit();
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-950">
      <div className="flex items-center gap-1 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-2 py-1.5">
        {TOOLS.map((t) => (
          <button
            key={t.cmd}
            type="button"
            title={t.label}
            data-testid={`rte-${t.cmd}`}
            onMouseDown={(e) => { e.preventDefault(); exec(t.cmd); }}
            className="grid place-items-center w-8 h-8 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            <t.icon className="w-4 h-4" />
          </button>
        ))}
        <button
          type="button"
          title="Insert link"
          data-testid="rte-link"
          onMouseDown={(e) => { e.preventDefault(); addLink(); }}
          className="grid place-items-center w-8 h-8 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        >
          <LinkIcon className="w-4 h-4" />
        </button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-testid={testid}
        className="rich-content min-h-[220px] max-h-[460px] overflow-y-auto px-4 py-3 text-sm text-slate-800 dark:text-slate-200 focus:outline-none"
      />
    </div>
  );
}
